import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, CircularProgress, Alert,
} from '@mui/material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ShowChart as ShowChartIcon,
} from '@mui/icons-material';
import { learningAnalyticsAPI, EnrollmentForecastData } from '../../services/learningAnalyticsApi';

const TREND_COLORS: Record<string, 'success' | 'info' | 'error'> = {
  growing: 'success',
  stable: 'info',
  declining: 'error',
};

const EnrollmentForecastTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<EnrollmentForecastData | null>(null);
  const [level, setLevel] = useState<string>('');
  const [district, setDistrict] = useState<string>('');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (level) params.level = parseInt(level);
      if (district) params.district = district;

      const response = await learningAnalyticsAPI.getEnrollmentForecast(params);
      setData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load enrollment forecast data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [level, district]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
  }

  if (!data) return null;

  const { summary, historical, forecasts, school_forecasts } = data;

  // Combine historical + forecast data for the chart
  const chartData = [
    ...historical.map(h => ({ year: h.year, enrollment: h.enrollment, type: 'historical' })),
    ...forecasts.map(f => ({ year: f.year, enrollment: f.enrollment, type: f.is_forecast ? 'forecast' : 'historical' })),
  ];

  // Separate historical and forecast values for dual-line rendering
  const combinedChart = chartData.map(point => ({
    year: point.year,
    historical: point.type === 'historical' ? point.enrollment : undefined,
    forecast: point.type === 'forecast' ? point.enrollment : undefined,
  }));

  // Bridge: add last historical point to forecast line so it connects
  const lastHistorical = historical[historical.length - 1];
  if (lastHistorical) {
    const bridgeIdx = combinedChart.findIndex(p => p.year === lastHistorical.year);
    if (bridgeIdx >= 0) {
      combinedChart[bridgeIdx].forecast = lastHistorical.enrollment;
    }
  }

  // Unique districts from school_forecasts for filter options
  const districts = Array.from(new Set(school_forecasts.map(s => s.district_name))).sort();

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="School Level" value={level}
              onChange={(e) => setLevel(e.target.value)}>
              <MenuItem value="">All Levels</MenuItem>
              <MenuItem value="3">Pre-Primary</MenuItem>
              <MenuItem value="1">Primary</MenuItem>
              <MenuItem value="2">Secondary</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="District" value={district}
              onChange={(e) => setDistrict(e.target.value)}
              placeholder="Type district name..."
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PeopleIcon color="primary" />
                <Typography variant="body2" color="textSecondary">Current Enrollment</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {summary.current_enrollment.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <TrendingUpIcon sx={{ color: '#1976d2' }} />
                <Typography variant="body2" color="textSecondary">Projected Next Year</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {summary.projected_next_year.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <ShowChartIcon sx={{ color: summary.annual_growth_rate >= 0 ? '#388e3c' : '#d32f2f' }} />
                <Typography variant="body2" color="textSecondary">Growth Rate</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold"
                sx={{ color: summary.annual_growth_rate >= 0 ? '#388e3c' : '#d32f2f' }}>
                {summary.annual_growth_rate >= 0 ? '+' : ''}{summary.annual_growth_rate.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <TrendingDownIcon color="error" />
                <Typography variant="body2" color="textSecondary">Schools Declining</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="error.main">
                {summary.schools_declining}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                of {summary.total_schools} schools
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Enrollment Forecast Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Enrollment Trend &amp; Forecast</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Solid line = historical data. Dashed line = projected forecast.
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={combinedChart}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis />
              <Tooltip formatter={(v: number) => v?.toLocaleString()} />
              <Legend />
              <Line type="monotone" dataKey="historical" stroke="#1976d2" strokeWidth={3}
                name="Historical" dot={{ r: 5 }} connectNulls={false} />
              <Line type="monotone" dataKey="forecast" stroke="#f57c00" strokeWidth={3}
                strokeDasharray="8 4" name="Forecast" dot={{ r: 5 }} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* School Forecasts Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>School-Level Enrollment Forecasts</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Individual school projections based on enrollment trends.
          </Typography>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>School Name</TableCell>
                  <TableCell>District</TableCell>
                  <TableCell align="right">Current</TableCell>
                  <TableCell align="right">Projected</TableCell>
                  <TableCell align="right">Growth Rate %</TableCell>
                  <TableCell align="center">Trend</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {school_forecasts.map((school) => (
                  <TableRow key={school.school_reg_number} hover>
                    <TableCell>{school.school_name}</TableCell>
                    <TableCell>{school.district_name}</TableCell>
                    <TableCell align="right">{school.current_enrollment.toLocaleString()}</TableCell>
                    <TableCell align="right">{school.projected_enrollment.toLocaleString()}</TableCell>
                    <TableCell align="right"
                      sx={{ color: school.annual_growth_rate >= 0 ? '#388e3c' : '#d32f2f' }}>
                      {school.annual_growth_rate >= 0 ? '+' : ''}{school.annual_growth_rate.toFixed(1)}%
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={school.trend}
                        size="small"
                        color={TREND_COLORS[school.trend] || 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EnrollmentForecastTab;
